import {
    CategoryScale,
    Chart,
    Legend,
    LinearScale,
    LineController,
    LineElement,
    PointElement,
    TimeScale, Title, Tooltip
} from "chart.js";
import 'chartjs-adapter-moment';
import verticalLinePlugin from "./plugins/verticalLinePlugin";
import chartAreaBorder from "./plugins/chartAreaBorder";
import {t} from "@lingui/macro";
import {getDefaultParams} from "./config";

Chart.register(LinearScale, LineController, CategoryScale, LineElement, PointElement, TimeScale, Legend, Title, Tooltip, verticalLinePlugin, chartAreaBorder);


export default class LineChart {
    constructor(ctx, config, maxLength = 144, days = 0) {
        this.days = days ? days : 1;
        this.visibleDays = Object.fromEntries(new Array(this.days).fill(true).map((x, i) => ([i.toString(), i === 0 ? x : !x])))
        this.chartOptions = config;
        this.chartData = Object.fromEntries(Object.keys(config).map(key => ([
            key, {
                labels: [],
                params: config[key].params,
                datasets: config[key].params.map(
                    param => (new Array(this.days).fill(0).map((v, i) => this.initialDataset(key, param, (this.days - i) - 1))
                    )).reduce((x, y) => x.concat(y))
            }])))

        this.activeChart = undefined;
        this.chart = new Chart(ctx, {type: 'line', data: {labels: [], datasets: []}, options: {}});
        this.maxLength = days ? 144 : maxLength;
        Chart.defaults.scale.grid.borderColor = "#000000";
        Chart.defaults.scale.grid.color = 'rgba(0, 0, 0, 0.4)';

        this.setDefaults(getDefaultParams(window.innerWidth))
    }

    setDefaults(params) {
        Chart.defaults.font.size = params.fontSize;
        Chart.defaults.font.weight = params.fontWeight;
        Chart.defaults.elements.line.borderWidth = params.lineWidth;
        Chart.defaults.scale.grid.borderWidth = params.scaleBorderWidth
        Chart.defaults.scale.grid.lineWidth = params.scaleLineWidth
        Chart.defaults.maintainAspectRatio = params.maintainAspectRatio;
    }

    initialDataset(key, param, i) {
        let label;
        if (this.days - i - 1 === 0) {
            label = t`Dzisiaj`;
        } else if (this.days - i - 1 === 1) {
            label = t`Wczoraj`;
        } else {
            label = t`${this.days - i - 1} dni temu`;
        }
        return {
            label: this.chartOptions[key].legend[param].label,
            borderColor: `rgba(${[0, 0, 0, (i + 1) / this.days].join(', ')})`,
            backgroundColor: `rgba(${[0, 0, 0, (i + 1) / this.days].join(', ')})`,
            param: param,
            index: i,
            minutes: (this.days - i - 1) * 1440,
            data: [],
            legendLabel: label,
        }
    }

    setActive(chartId) {
        if (!this.chart) return;
        if (!(chartId in this.chartData)) {
            chartId = Object.keys(this.chartData)[0]
        }
        this.activeChart = chartId;
        this.chart.data.labels = this.chartData[chartId].labels;
        this.chart.data.datasets = this.chartData[chartId].datasets;
        this.chart.options = this.chartOptions[chartId].options;
        for (const [key, visibility] of Object.entries(this.visibleDays)) {
            this.chart.setDatasetVisibility(parseInt(key), visibility)
        }
        this.chart.update();
    }

    setDatasetVisibility(key, value) {
        this.visibleDays[key.toString()] = value;
        this.chart.setDatasetVisibility(key, value);
        this.chart.update()
    }

    hide(key) {
        this.setDatasetVisibility(key, false)
    }

    show(key) {
        this.setDatasetVisibility(key, true)
    }

    splitDays(data, length = this.maxLength) {
        const chunks = [], n = data.length;
        let i = data.length - (length * this.days)
        if (i < 0) {
            data.unshift(...new Array(-1 * i).fill(null));
            i = 0;
        }

        while (i < data.length) {
            chunks.push(data.slice(i, i += length));
        }
        return chunks
    }

    splitLabels(labels) {
        let l;
        if (labels.length >= this.maxLength) {
            l = labels.slice(labels.length - this.maxLength)

        } else {
            l = labels
        }
        return l.map(x => new Date(x))
    }

    setData(data) {
        if (!this.chart) return;

        const labels = this.splitLabels(data.data['date'])
        for (const chartData of Object.values(this.chartData)) {
            const params = chartData['params'].filter(el => (Object.keys(data.data).indexOf(el) !== -1));
            if (params.length) {
                const d = Object.fromEntries(params.map(p => ([p, this.splitDays(data.data[p])])))
                for (const dataset of chartData.datasets) {
                    dataset.data = d[dataset.param][dataset.index]
                }
                chartData.labels = [...labels];
            }
        }

        this.chart.data.labels = this.chartData[this.activeChart].labels;
        this.chart.data.datasets = this.chartData[this.activeChart].datasets;
        this.chart.update();
    }

    pushData(data) {
        if (!this.chart) return;

        const label = new Date(data.data['date']);

        const filterParams = (el) => (Object.keys(data.data).indexOf(el) !== -1)
        const filterLabels = (d) => (d.labels[d.labels.length - 1] < label)
        const updatedCharts = []

        for (const key of Object.keys(this.chartData).filter(key => filterLabels(this.chartData[key]))) {
            const params = this.chartData[key]['params'].filter(filterParams)
            if (params.length) {
                const d = Object.fromEntries(params.map(p => [p, Array(this.days).fill(null)]));
                for (const dataset of this.chartData[key].datasets) {
                    if (dataset.index === this.days - 1) {
                        d[dataset['param']][this.days - 1] = data.data[dataset['param']]
                    }
                    if (dataset.index > 0) {
                        d[dataset['param']][dataset.index - 1] = dataset.data[0]
                    }
                }
                if (key === this.activeChart) {
                    this.chart.data.labels.push(label)
                    for (const dataset of this.chart.data.datasets) {
                        dataset.data.push(d[dataset['param']][dataset['index']])
                    }
                    this.chart.update();
                } else {
                    this.chartData[key].labels.push(label);
                    for (const dataset of this.chartData[key].datasets) {
                        dataset.data.push(d[dataset['param']][dataset['index']])
                    }
                }
                updatedCharts.push(key);
            }
        }

        for (const key of updatedCharts) {
            if (key === this.activeChart && this.chart.data.labels.length > this.maxLength) {
                this.chart.data.labels.splice(0, 1);
                this.chart.data.datasets.forEach(dataset => {
                    dataset.data.splice(0, 1)
                });
                this.chart.update()
            } else if (this.chartData[key].labels.length > this.maxLength) {
                this.chartData[key].labels.splice(0, 1);
                this.chartData[key].datasets.forEach(dataset => {
                    dataset.data.splice(0, 1)
                });
            }
        }
    }


    destroy() {
        this.chart.destroy();
        delete this.chart
    }
}